import { Controller } from "@hotwired/stimulus"
import { followBottom } from "../utils/chat_scroll"
import { createConsumer } from "@rails/actioncable"

// Watch Party — keeps a YouTube player in sync across everyone in a room and
// drives the live chat + presence sidebar over ActionCable (WatchRoomChannel).
//
// Roles: the HOST is the single source of truth — its play/pause/seek/video
// changes broadcast a `sync`; guests reconcile (load a changed video, seek
// when drift > 2s, match play/pause) but their own manual fiddling is left
// alone until the next host sync re-aligns them.
//
// Graceful degradation: if the cable can't connect or the YouTube API doesn't
// load, the video still plays standalone (just unsynced) and a status note
// explains it — never a blank/broken page. All cable/player use is wrapped
// defensively.
export default class extends Controller {
    static targets = ["player", "messages", "chatInput", "presence", "viewerCount", "status", "videoInput"]

    connect() {
        this.config = this.#readConfig()
        this.isHost = this.config.host === "true"
        this.token = this.config.token
        this.syncing = false
        this.ready = false

        this.#scrollMessages()
        this.#loadYouTube()
        this.#connectCable()
    }

    disconnect() {
        clearInterval(this.tick)
        clearInterval(this.pingTimer)
        clearTimeout(this.ytTimeout)
        if (this.subscription) { try { this.subscription.unsubscribe() } catch (e) { /* noop */ } }
        if (this.consumer) { try { this.consumer.disconnect() } catch (e) { /* noop */ } }
        if (this.player && this.player.destroy) { try { this.player.destroy() } catch (e) { /* noop */ } }
    }

    #readConfig() {
        const el = this.element.querySelector("#watch-party-config")
        return el ? { ...el.dataset } : {}
    }

    // --- YouTube IFrame API -------------------------------------------------

    #loadYouTube() {
        if (window.YT && window.YT.Player) { this.#buildPlayer(); return }

        // Only ever inject the API script once per page; chain onto any
        // existing readiness callback so multiple controllers cooperate.
        const previous = window.onYouTubeIframeAPIReady
        window.onYouTubeIframeAPIReady = () => {
            if (typeof previous === "function") { try { previous() } catch (e) { /* noop */ } }
            this.#buildPlayer()
        }
        if (!document.getElementById("youtube-iframe-api")) {
            const tag = document.createElement("script")
            tag.id = "youtube-iframe-api"
            tag.src = "https://www.youtube.com/iframe_api"
            tag.async = true
            tag.onerror = () => this.#setStatus("Video player unavailable - try reloading.")
            document.head.appendChild(tag)
        }
        // If the API never arrives, say so rather than showing an empty stage.
        this.ytTimeout = setTimeout(() => {
            if (!this.ready) this.#setStatus("Video player is taking a while to load…")
        }, 9000)
    }

    #buildPlayer() {
        if (!this.hasPlayerTarget || this.player) return
        try {
            this.player = new YT.Player(this.playerTarget, {
                host: "https://www.youtube-nocookie.com",
                videoId: this.config.videoId || "",
                playerVars: { rel: 0, modestbranding: 1, playsinline: 1 },
                events: {
                    onReady: () => this.#onPlayerReady(),
                    onStateChange: (e) => this.#onStateChange(e)
                }
            })
        } catch (e) {
            this.#setStatus("Could not start the video player.")
        }
    }

    #onPlayerReady() {
        this.ready = true
        clearTimeout(this.ytTimeout)

        // Late-join: start near the room's live spot (server-computed
        // effective position). Only the host auto-plays; guests wait for a
        // sync so they don't fight the host on load.
        const pos = parseFloat(this.config.position || "0")
        if (this.config.videoId && pos > 0) {
            try { this.player.seekTo(pos, true) } catch (e) { /* noop */ }
        }
        if (this.config.state === "playing") {
            try { this.player.playVideo() } catch (e) { /* noop */ }
        }
        if (this.isHost) {
            this.tick = setInterval(() => this.#hostTick(), 4000)
        }
    }

    #onStateChange(e) {
        if (!this.isHost || this.syncing) return
        if (e.data === YT.PlayerState.PLAYING || e.data === YT.PlayerState.PAUSED) {
            this.#sendSync()
        }
    }

    #hostTick() {
        if (!this.isHost || !this.ready) return
        try {
            if (this.player.getPlayerState() === YT.PlayerState.PLAYING) this.#sendSync()
        } catch (e) { /* noop */ }
    }

    #sendSync() {
        if (!this.subscription || !this.ready) return
        let position = 0
        let state = "paused"
        let videoId = this.config.videoId
        try {
            position = this.player.getCurrentTime() || 0
            state = this.player.getPlayerState() === YT.PlayerState.PLAYING ? "playing" : "paused"
            const data = this.player.getVideoData && this.player.getVideoData()
            if (data && data.video_id) videoId = data.video_id
        } catch (e) { return }
        this.config.videoId = videoId
        this.subscription.perform("sync", { video_id: videoId, title: null, state, position })
    }

    // Host-only: load a different video and sync it to everyone.
    changeVideo(event) {
        event.preventDefault()
        if (!this.isHost || !this.hasVideoInputTarget) return
        const id = this.#extractId(this.videoInputTarget.value)
        if (!id) { this.#setStatus("Paste a YouTube link or an 11-character id."); return }

        this.config.videoId = id
        try { this.player.loadVideoById(id) } catch (e) { /* noop */ }
        this.videoInputTarget.value = ""
        this.#setStatus("")
        // Give the player a beat to switch, then broadcast the new video id.
        setTimeout(() => this.#sendSync(), 700)
    }

    #extractId(raw) {
        const value = (raw || "").trim()
        if (/^[A-Za-z0-9_-]{11}$/.test(value)) return value
        const match = value.match(/youtu\.be\/([A-Za-z0-9_-]{11})/) ||
            value.match(/[?&]v=([A-Za-z0-9_-]{11})/) ||
            value.match(/\/embed\/([A-Za-z0-9_-]{11})/) ||
            value.match(/([A-Za-z0-9_-]{11})/)
        return match ? match[1] : null
    }

    // --- ActionCable --------------------------------------------------------

    #connectCable() {
        if (typeof createConsumer !== "function") { this.#degrade(); return }
        try {
            this.consumer = createConsumer()
            this.subscription = this.consumer.subscriptions.create(
                { channel: "WatchRoomChannel", token: this.token },
                {
                    connected: () => this.#onConnected(),
                    disconnected: () => this.#setStatus("Reconnecting…"),
                    rejected: () => this.#setStatus("You can't join this room."),
                    received: (data) => this.#onReceived(data)
                }
            )
        } catch (e) {
            this.#degrade()
        }
    }

    #onConnected() {
        this.#setStatus("")
        // Heartbeat keeps this viewer in the presence list and refreshes it.
        this.pingTimer = setInterval(() => {
            if (this.subscription) { try { this.subscription.perform("ping", {}) } catch (e) { /* noop */ } }
        }, 30000)
    }

    #onReceived(data) {
        if (!data || !data.type) return
        if (data.type === "sync") { this.#applySync(data); return }
        if (data.type === "chat") { this.#appendChat(data.html); return }
        if (data.type === "reaction") { this.#applyReaction(data); return }
        if (data.type === "presence") { this.#applyPresence(data) }
    }

    #applySync(data) {
        // The host never reconciles against its own broadcast.
        if (this.isHost || !this.ready || !this.player) return
        this.syncing = true
        try {
            const current = (this.player.getVideoData && this.player.getVideoData().video_id) || this.config.videoId
            const target = this.#targetPosition(data)
            if (data.video_id && data.video_id !== current) {
                this.config.videoId = data.video_id
                this.player.loadVideoById(data.video_id, target)
            } else {
                const now = this.player.getCurrentTime() || 0
                if (Math.abs(now - target) > 2) this.player.seekTo(target, true)
            }
            if (data.state === "playing") this.player.playVideo()
            else this.player.pauseVideo()
        } catch (e) { /* noop */ }
        // Release the reconcile guard after the player settles so the guest's
        // own resulting state-change events don't echo back.
        setTimeout(() => { this.syncing = false }, 800)
    }

    // Estimate where the host is right now: the broadcast position plus the
    // wall time elapsed since their update if they were playing.
    #targetPosition(data) {
        let pos = parseFloat(data.position || 0)
        if (data.state === "playing" && data.updated_at) {
            const elapsed = (Date.now() / 1000) - parseFloat(data.updated_at)
            if (elapsed > 0 && elapsed < 3600) pos += elapsed
        }
        return Math.max(pos, 0)
    }

    // --- Chat + presence ----------------------------------------------------

    chat(event) {
        event.preventDefault()
        if (!this.hasChatInputTarget) return
        const body = this.chatInputTarget.value.trim()
        if (!body) return
        if (!this.subscription) { this.#setStatus("Chat is offline right now."); return }
        try {
            this.subscription.perform("chat", { body })
            this.chatInputTarget.value = ""
        } catch (e) {
            this.#setStatus("Message not sent - reconnecting.")
        }
    }

    #appendChat(html) {
        if (!this.hasMessagesTarget || !html) return
        this.messagesTarget.insertAdjacentHTML("beforeend", html)
        this.#scrollMessages()
    }

    // Bound on this controller's element (.watch-shell, see
    // watch/_shell.html.slim's `action` local) for "emoji-picker:picked",
    // dispatched by the shared reaction picker (watch_party/_reactions
    // renders it in event-only mode — no react_url, so it never POSTs and
    // only this cable send happens). The picker's own message id comes from
    // its .rxp root's data-message-id (watch_party/_reactions's `data:`
    // local); event.target is inside that picker, so the closest lookup
    // finds it regardless of which part of the picker was clicked. A tap on
    // an existing chip raises the same event (shared/_chat_reactions in relay
    // mode), resolved by the row's own data-message-id. The server toggles and
    // re-broadcasts the row, which #applyReaction swaps in.
    reactPicked(event) {
        const emoji = event.detail && event.detail.emoji
        const host = event.target.closest("[data-message-id]")
        this.#sendReaction(host && host.dataset.messageId, emoji)
    }

    #sendReaction(messageId, emoji) {
        if (!emoji || !messageId) return
        if (!this.subscription) { this.#setStatus("Reactions are offline right now."); return }
        try {
            this.subscription.perform("react", { message_id: messageId, emoji })
        } catch (e) {
            this.#setStatus("Reaction not sent - reconnecting.")
        }
    }

    #applyReaction(data) {
        if (!this.hasMessagesTarget || !data.html || !data.id) return
        const row = this.messagesTarget.querySelector(`#wp_msg_${data.id}_reactions`)
        if (row) row.outerHTML = data.html
    }

    #applyPresence(data) {
        if (this.hasPresenceTarget && data.html) this.presenceTarget.innerHTML = data.html
        if (this.hasViewerCountTarget && typeof data.count === "number") {
            this.viewerCountTarget.textContent = data.count
        }
    }

    #scrollMessages() {
        if (this.hasMessagesTarget) followBottom(this, this.messagesTarget).contentChanged()
    }

    #setStatus(text) {
        if (!this.hasStatusTarget) return
        this.statusTarget.textContent = text || ""
        this.statusTarget.hidden = !text
    }

    #degrade() {
        this.#setStatus("Live sync is offline - the video plays on its own.")
    }
}
